import React, { useState, useEffect } from 'react'
import { School, Building2, Plus, Check, Search, ChevronDown, ChevronRight, Trash2, MessageSquarePlus } from 'lucide-react'
import { apiClient } from '@/utils/api'
import toast from 'react-hot-toast'

// Types
interface FamilyCustomSchool {
  id: string
  name: string
  district: string
  type: string
  address?: string
  phone?: string
  website?: string
}

interface PlatformDistrict {
  id: string
  district_name: string
  district_code?: string
  state: string
  county?: string
  website?: string
  timezone: string
  school_count?: number
  event_count?: number
}

interface PlatformSchool {
  id: string
  district_id?: string
  school_name: string
  school_type?: string
  address?: string
  city?: string
  state?: string
  zip_code?: string
  phone?: string
  website?: string
  district_name?: string
  event_count?: number
}

interface FamilySchool {
  id: string
  platform_school_id: string
  nickname?: string
  color: string
  is_active: boolean
  school_name?: string
  school_type?: string
  district_name?: string
}

interface FamilyDistrict {
  id: string
  platform_district_id: string
  nickname?: string
  color: string
  is_active: boolean
  district_name?: string
  state?: string
}

export default function SchoolBrowser() {
  const [loading, setLoading] = useState(true)
  const [districts, setDistricts] = useState<PlatformDistrict[]>([])
  const [schools, setSchools] = useState<PlatformSchool[]>([])
  const [schoolSubscriptions, setSchoolSubscriptions] = useState<FamilySchool[]>([])
  const [districtSubscriptions, setDistrictSubscriptions] = useState<FamilyDistrict[]>([])
  const [selectedState, setSelectedState] = useState<string>('')
  const [searchQuery, setSearchQuery] = useState('')
  const [expandedDistrict, setExpandedDistrict] = useState<string | null>(null)
  const [subscribing, setSubscribing] = useState<string | null>(null)
  const [customSchools, setCustomSchools] = useState<FamilyCustomSchool[]>([])
  const [showAddCustom, setShowAddCustom] = useState(false)
  const [customSchoolForm, setCustomSchoolForm] = useState<Partial<FamilyCustomSchool>>({ type: 'elementary' })
  const [showRequestForm, setShowRequestForm] = useState(false)
  const [requestMessage, setRequestMessage] = useState('')
  const [schoolFeatureEnabled, setSchoolFeatureEnabled] = useState<boolean | null>(null)
  const [enablingFeature, setEnablingFeature] = useState(false)
  const [familyState, setFamilyState] = useState<string | null>(null)
  const [familyCity, setFamilyCity] = useState<string | null>(null)

  // Load districts and subscriptions on mount
  useEffect(() => {
    loadData()
  }, [])

  // Re-fetch districts when state or search changes (debounced for search)
  // Skip initial render (loading=true) to let loadData set the family state first
  const [dataLoaded, setDataLoaded] = useState(false)
  useEffect(() => {
    if (!dataLoaded) return
    const timer = setTimeout(() => {
      loadDistricts(selectedState || undefined, searchQuery || undefined)
    }, searchQuery ? 300 : 0)
    return () => clearTimeout(timer)
  }, [selectedState, searchQuery, dataLoaded])

  const loadData = async () => {
    try {
      // Check if school integration feature is enabled
      try {
        const featuresResponse = await apiClient.get('/api/family/features')
        if (featuresResponse.success && featuresResponse.data) {
          const schoolFeature = featuresResponse.data.find(
            (f: any) => f.feature_key === 'school_integration'
          )
          setSchoolFeatureEnabled(schoolFeature?.enabled ?? false)
        }
      } catch {
        // If we can't check, assume enabled so the page still works
        setSchoolFeatureEnabled(true)
      }

      // Get family's home state and city for default district filtering
      let homeState: string | undefined
      let homeCity: string | undefined
      try {
        const places = await apiClient.get('/api/places')
        const home = (places.places || places || []).find((p: any) => p.place_type === 'home' && p.state)
        if (home?.state) {
          homeState = home.state
          homeCity = home.city
          setFamilyState(home.state)
          setFamilyCity(home.city || null)
          setSelectedState(home.state)
        }
      } catch {
        // No places data, fall back to all states
      }

      await Promise.all([
        loadDistricts(homeState),
        loadSchoolSubscriptions(),
        loadDistrictSubscriptions(),
        loadCustomSchools()
      ])
      setDataLoaded(true)
    } catch (error) {
      console.error('Failed to load school data:', error)
    } finally {
      setLoading(false)
    }
  }

  const enableSchoolFeature = async () => {
    setEnablingFeature(true)
    try {
      const response = await apiClient.put('/api/family/features/school_integration', {
        enabled: true
      })
      if (response.success) {
        setSchoolFeatureEnabled(true)
        toast.success('School integration enabled')
      } else {
        toast.error('Failed to enable school integration')
      }
    } catch {
      toast.error('Failed to enable school integration')
    } finally {
      setEnablingFeature(false)
    }
  }

  const loadDistricts = async (state?: string, search?: string) => {
    try {
      const params = new URLSearchParams()
      if (state) params.append('state', state)
      if (search) params.append('search', search)
      const data = await apiClient.get(`/api/districts/nearby?${params.toString()}`)
      setDistricts(data.districts || [])
    } catch (error) {
      console.error('Failed to load districts:', error)
    }
  }

  const loadSchoolsForDistrict = async (districtId: string) => {
    try {
      const data = await apiClient.get(`/api/platform/schools?district_id=${districtId}`)
      setSchools(data.schools || [])
    } catch (error) {
      console.error('Failed to load schools:', error)
    }
  }

  const loadSchoolSubscriptions = async () => {
    try {
      const data = await apiClient.get('/api/schools/subscriptions')
      setSchoolSubscriptions(data.subscriptions || [])
    } catch (error) {
      console.error('Failed to load school subscriptions:', error)
    }
  }

  const loadDistrictSubscriptions = async () => {
    try {
      const data = await apiClient.get('/api/districts/subscriptions')
      setDistrictSubscriptions(data.subscriptions || [])
    } catch (error) {
      console.error('Failed to load district subscriptions:', error)
    }
  }

  const loadCustomSchools = async () => {
    try {
      const data = await apiClient.get('/api/schools')
      setCustomSchools(data.schools || [])
    } catch (error) {
      console.error('Failed to load custom schools:', error)
    }
  }

  const saveCustomSchool = async () => {
    if (!customSchoolForm.name) {
      toast.error('School name is required')
      return
    }
    try {
      await apiClient.post('/api/schools', customSchoolForm)
      toast.success('School added')
      setShowAddCustom(false)
      setCustomSchoolForm({ type: 'elementary' })
      await loadCustomSchools()
    } catch (error) {
      console.error('Failed to save school:', error)
      toast.error('Failed to add school')
    }
  }

  const deleteCustomSchool = async (schoolId: string) => {
    if (!confirm('Are you sure you want to remove this school?')) return
    try {
      await apiClient.delete(`/api/schools/${schoolId}`)
      toast.success('School removed')
      await loadCustomSchools()
    } catch (error) {
      console.error('Failed to delete school:', error)
      toast.error('Failed to remove school')
    }
  }

  const submitSchoolRequest = async () => {
    if (!requestMessage.trim()) {
      toast.error('Please describe the school or district you need')
      return
    }
    try {
      await apiClient.post('/api/feedback/school-request', {
        message: requestMessage,
        subject: 'School/District Request'
      })
      toast.success('Request submitted. We\'ll add your school as soon as possible.')
      setShowRequestForm(false)
      setRequestMessage('')
    } catch (error) {
      console.error('Failed to submit school request:', error)
      toast.error('Failed to submit request. Please try again.')
    }
  }

  const handleSubscribeSchool = async (schoolId: string) => {
    setSubscribing(`school-${schoolId}`)
    try {
      await apiClient.post('/api/schools/subscribe', {
        platform_school_id: schoolId,
        color: '#3B82F6' // Default blue color
      })
      await loadSchoolSubscriptions()
    } catch (error: any) {
      console.error('Failed to subscribe:', error)
      alert(`Failed to subscribe: ${error?.message || 'Unknown error'}`)
    } finally {
      setSubscribing(null)
    }
  }

  const handleSubscribeDistrict = async (districtId: string, e: React.MouseEvent) => {
    e.stopPropagation() // Prevent district expansion
    setSubscribing(`district-${districtId}`)
    try {
      await apiClient.post('/api/districts/subscribe', {
        platform_district_id: districtId,
        color: '#10B981' // Default green color for districts
      })
      await loadDistrictSubscriptions()
    } catch (error: any) {
      console.error('Failed to subscribe to district:', error)
      alert(`Failed to subscribe: ${error?.message || 'Unknown error'}`)
    } finally {
      setSubscribing(null)
    }
  }

  const handleUnsubscribeSchool = async (subscriptionId: string) => {
    if (!confirm('Are you sure you want to unsubscribe from this school?')) {
      return
    }

    try {
      await apiClient.delete(`/api/schools/subscriptions/${subscriptionId}`)
      await loadSchoolSubscriptions()
    } catch (error: any) {
      console.error('Failed to unsubscribe:', error)
      alert(`Failed to unsubscribe: ${error?.message || 'Unknown error'}`)
    }
  }

  const handleUnsubscribeDistrict = async (subscriptionId: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation()
    if (!confirm('Are you sure you want to unsubscribe from this district?')) {
      return
    }

    try {
      await apiClient.delete(`/api/districts/subscriptions/${subscriptionId}`)
      await loadDistrictSubscriptions()
    } catch (error: any) {
      console.error('Failed to unsubscribe:', error)
      alert(`Failed to unsubscribe: ${error?.message || 'Unknown error'}`)
    }
  }

  const handleDistrictClick = async (districtId: string) => {
    if (expandedDistrict === districtId) {
      setExpandedDistrict(null)
      setSchools([])
    } else {
      setExpandedDistrict(districtId)
      await loadSchoolsForDistrict(districtId)
    }
  }

  const isSchoolSubscribed = (schoolId: string) => {
    return schoolSubscriptions.some(sub => sub.platform_school_id === schoolId && sub.is_active)
  }

  const isDistrictSubscribed = (districtId: string) => {
    return districtSubscriptions.some(sub => sub.platform_district_id === districtId && sub.is_active)
  }

  const getSchoolSubscriptionId = (schoolId: string) => {
    const sub = schoolSubscriptions.find(s => s.platform_school_id === schoolId && s.is_active)
    return sub?.id
  }

  const getDistrictSubscriptionId = (districtId: string) => {
    const sub = districtSubscriptions.find(s => s.platform_district_id === districtId && s.is_active)
    return sub?.id
  }

  // Districts are filtered server-side via state/search params
  const filteredDistricts = districts

  // US states for the dropdown
  const states = [
    'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
    'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
    'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
    'VA','WA','WV','WI','WY','DC'
  ]

  const totalSubscriptions = schoolSubscriptions.length + districtSubscriptions.length

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-gray-500">Loading schools...</div>
      </div>
    )
  }

  // Show enable prompt if school integration feature is off
  if (schoolFeatureEnabled === false) {
    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">School Calendar Integration</h3>
          <p className="text-sm text-gray-600">
            Subscribe to your children's schools to automatically sync their calendar events, holidays, and early dismissals.
          </p>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-blue-100 rounded-lg">
              <School className="w-6 h-6 text-blue-600" />
            </div>
            <div className="flex-1">
              <h4 className="font-medium text-blue-900 mb-1">School Integration is disabled</h4>
              <p className="text-sm text-blue-700 mb-4">
                Enable the School Integration feature to browse and subscribe to school calendars.
                You can also manage this in Settings &gt; System &gt; Features.
              </p>
              <button
                onClick={enableSchoolFeature}
                disabled={enablingFeature}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {enablingFeature ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    Enabling...
                  </>
                ) : (
                  'Enable School Integration'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Show message if no school data is available
  if (districts.length === 0 && totalSubscriptions === 0) {
    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">School Calendar Integration</h3>
          <p className="text-sm text-gray-600">
            Subscribe to your children's schools to automatically sync their calendar events, holidays, and early dismissals.
          </p>
        </div>
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <p className="text-yellow-800">
            No school districts are currently available in the system.
          </p>
          <p className="text-sm text-yellow-700 mt-2">
            School calendar data is being added. Check back soon!
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">School Calendar Integration</h3>
        <p className="text-sm text-gray-600">
          Subscribe to districts for district-wide events, or individual schools for school-specific calendars.
        </p>
      </div>

      {/* My Subscriptions */}
      {totalSubscriptions > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-blue-900 mb-3 flex items-center gap-2">
            <Check className="h-4 w-4" />
            My Subscriptions ({totalSubscriptions})
          </h4>
          <div className="space-y-2">
            {/* District Subscriptions */}
            {districtSubscriptions.map(sub => (
              <div
                key={`district-${sub.id}`}
                className="bg-white rounded-lg p-3 flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <div
                    className="w-1 h-12 rounded-full"
                    style={{ backgroundColor: sub.color }}
                  />
                  <Building2 className="h-5 w-5 text-emerald-600" />
                  <div>
                    <div className="font-medium text-gray-900">
                      {sub.nickname || sub.district_name}
                    </div>
                    <div className="text-sm text-gray-600">
                      {sub.state} • District-wide events
                    </div>
                  </div>
                </div>
                <button
                  onClick={(e) => handleUnsubscribeDistrict(sub.id, e)}
                  className="px-3 py-1 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  Unsubscribe
                </button>
              </div>
            ))}
            {/* School Subscriptions */}
            {schoolSubscriptions.map(sub => (
              <div
                key={`school-${sub.id}`}
                className="bg-white rounded-lg p-3 flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <div
                    className="w-1 h-12 rounded-full"
                    style={{ backgroundColor: sub.color }}
                  />
                  <School className="h-5 w-5 text-blue-600" />
                  <div>
                    <div className="font-medium text-gray-900">
                      {sub.nickname || sub.school_name}
                    </div>
                    <div className="text-sm text-gray-600">
                      {sub.district_name && `${sub.district_name} • `}
                      {sub.school_type && `${sub.school_type}`}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => handleUnsubscribeSchool(sub.id)}
                  className="px-3 py-1 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  Unsubscribe
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* My Custom Schools */}
      {customSchools.length > 0 && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-purple-900 mb-3 flex items-center gap-2">
            <School className="h-4 w-4" />
            My Custom Schools ({customSchools.length})
          </h4>
          <div className="space-y-2">
            {customSchools.map(school => (
              <div
                key={school.id}
                className="bg-white rounded-lg p-3 flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <School className="h-5 w-5 text-purple-600" />
                  <div>
                    <div className="font-medium text-gray-900">{school.name}</div>
                    <div className="text-sm text-gray-600">
                      {school.district && `${school.district} • `}
                      {school.type}
                      {school.address && ` • ${school.address}`}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => deleteCustomSchool(school.id)}
                  className="p-1 text-red-600 hover:bg-red-50 rounded"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
          <p className="text-xs text-purple-700 mt-3">
            Custom schools are for your family's reference. Subscribe to a district above for automatic calendar events.
          </p>
        </div>
      )}

      {/* Search and Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex gap-4 mb-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              State
            </label>
            <select
              value={selectedState}
              onChange={(e) => setSelectedState(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All States</option>
              {states.map(state => (
                <option key={state} value={state}>{state}</option>
              ))}
            </select>
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search Districts
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by district name..."
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Districts List */}
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {filteredDistricts.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No districts found. Select a different state or search criteria.
            </div>
          ) : (
            filteredDistricts.map(district => {
              const districtSubscribed = isDistrictSubscribed(district.id)
              const districtSubId = getDistrictSubscriptionId(district.id)
              const isDistrictLoading = subscribing === `district-${district.id}`

              return (
                <div key={district.id} className="border border-gray-200 rounded-lg overflow-hidden">
                  {/* District Header */}
                  <div className="px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors">
                    <button
                      onClick={() => handleDistrictClick(district.id)}
                      className="flex items-center gap-3 flex-1 text-left"
                    >
                      {expandedDistrict === district.id ? (
                        <ChevronDown className="h-5 w-5 text-gray-400" />
                      ) : (
                        <ChevronRight className="h-5 w-5 text-gray-400" />
                      )}
                      <Building2 className="h-5 w-5 text-blue-600" />
                      <div>
                        <div className="font-medium text-gray-900">{district.district_name}</div>
                        <div className="text-sm text-gray-600">
                          {district.state}
                          {district.county && ` • ${district.county}`}
                          {district.school_count !== undefined && ` • ${district.school_count} schools`}
                          {district.event_count !== undefined && district.event_count > 0 && (
                            <span className="ml-1 text-emerald-600">
                              • {district.event_count} district events
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                    {/* District Subscribe Button */}
                    {districtSubscribed ? (
                      <button
                        onClick={(e) => districtSubId && handleUnsubscribeDistrict(districtSubId, e)}
                        className="px-3 py-1 text-sm bg-emerald-100 text-emerald-700 rounded-lg flex items-center gap-2 hover:bg-emerald-200 transition-colors"
                      >
                        <Check className="h-4 w-4" />
                        District
                      </button>
                    ) : (
                      <button
                        onClick={(e) => handleSubscribeDistrict(district.id, e)}
                        disabled={isDistrictLoading}
                        className="px-3 py-1 text-sm bg-emerald-600 text-white rounded-lg flex items-center gap-2 hover:bg-emerald-700 transition-colors disabled:opacity-50"
                      >
                        {isDistrictLoading ? (
                          <>Loading...</>
                        ) : (
                          <>
                            <Plus className="h-4 w-4" />
                            District
                          </>
                        )}
                      </button>
                    )}
                  </div>

                  {/* Schools List (when expanded) */}
                  {expandedDistrict === district.id && (
                    <div className="bg-gray-50 border-t border-gray-200">
                      {schools.length === 0 ? (
                        <div className="px-4 py-8 text-center text-gray-500">
                          No schools found in this district. Subscribe to the district for district-wide events.
                        </div>
                      ) : (
                        <div className="divide-y divide-gray-200">
                          {schools.map(school => {
                            const subscribed = isSchoolSubscribed(school.id)
                            const subId = getSchoolSubscriptionId(school.id)
                            const isLoading = subscribing === `school-${school.id}`

                            return (
                              <div
                                key={school.id}
                                className="px-4 py-3 flex items-center justify-between hover:bg-white transition-colors"
                              >
                                <div className="flex items-center gap-3 flex-1">
                                  <School className="h-5 w-5 text-gray-400 ml-8" />
                                  <div className="flex-1">
                                    <div className="font-medium text-gray-900">{school.school_name}</div>
                                    <div className="text-sm text-gray-600">
                                      {school.school_type && `${school.school_type} • `}
                                      {school.city && `${school.city}, `}
                                      {school.state}
                                      {school.event_count !== undefined && school.event_count > 0 && (
                                        <span className="ml-2 text-blue-600">
                                          {school.event_count} events
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                {subscribed ? (
                                  <button
                                    onClick={() => subId && handleUnsubscribeSchool(subId)}
                                    className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-lg flex items-center gap-2 hover:bg-blue-200 transition-colors"
                                  >
                                    <Check className="h-4 w-4" />
                                    School
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => handleSubscribeSchool(school.id)}
                                    disabled={isLoading}
                                    className="px-3 py-1 text-sm bg-blue-600 text-white rounded-lg flex items-center gap-2 hover:bg-blue-700 transition-colors disabled:opacity-50"
                                  >
                                    {isLoading ? (
                                      <>Loading...</>
                                    ) : (
                                      <>
                                        <Plus className="h-4 w-4" />
                                        School
                                      </>
                                    )}
                                  </button>
                                )}
                              </div>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )
            })
          )}
        </div>
      </div>

      {/* Can't Find Your School */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-amber-900 mb-2">Can't find your school?</h4>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => setShowAddCustom(true)}
            className="px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Add Custom School
          </button>
          <button
            onClick={() => setShowRequestForm(true)}
            className="px-4 py-2 text-sm bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors flex items-center gap-2"
          >
            <MessageSquarePlus className="h-4 w-4" />
            Request School/District
          </button>
        </div>
        <p className="text-xs text-amber-700 mt-2">
          Add a custom school for immediate use, or request we add your school's calendar for automatic event syncing.
        </p>
      </div>

      {/* Add Custom School Form */}
      {showAddCustom && (
        <div className="bg-white border border-purple-200 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-gray-900 mb-4">Add Custom School</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">School Name *</label>
              <input
                type="text"
                value={customSchoolForm.name || ''}
                onChange={(e) => setCustomSchoolForm({ ...customSchoolForm, name: e.target.value })}
                placeholder="e.g., Lincoln Elementary"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">District</label>
              <input
                type="text"
                value={customSchoolForm.district || ''}
                onChange={(e) => setCustomSchoolForm({ ...customSchoolForm, district: e.target.value })}
                placeholder="e.g., Cobb County"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select
                value={customSchoolForm.type || 'elementary'}
                onChange={(e) => setCustomSchoolForm({ ...customSchoolForm, type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="elementary">Elementary</option>
                <option value="middle">Middle School</option>
                <option value="high">High School</option>
                <option value="private">Private School</option>
                <option value="college">College/University</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
              <input
                type="text"
                value={customSchoolForm.address || ''}
                onChange={(e) => setCustomSchoolForm({ ...customSchoolForm, address: e.target.value })}
                placeholder="Optional"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
          </div>
          <div className="flex justify-end space-x-2 mt-4">
            <button
              onClick={() => { setShowAddCustom(false); setCustomSchoolForm({ type: 'elementary' }) }}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              onClick={saveCustomSchool}
              className="px-4 py-2 text-white bg-purple-600 rounded-md hover:bg-purple-700"
            >
              Add School
            </button>
          </div>
        </div>
      )}

      {/* Request School Form */}
      {showRequestForm && (
        <div className="bg-white border border-amber-200 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-gray-900 mb-2">Request a School or District</h4>
          <p className="text-sm text-gray-600 mb-3">
            Tell us which school or district you'd like us to add. We'll set up calendar integration so events sync automatically.
          </p>
          <textarea
            value={requestMessage}
            onChange={(e) => setRequestMessage(e.target.value)}
            placeholder="e.g., Please add Marietta City Schools district in Georgia, specifically Park Street Elementary..."
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 h-24"
          />
          <div className="flex justify-end space-x-2 mt-3">
            <button
              onClick={() => { setShowRequestForm(false); setRequestMessage('') }}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              onClick={submitSchoolRequest}
              className="px-4 py-2 text-white bg-amber-600 rounded-md hover:bg-amber-700"
            >
              Submit Request
            </button>
          </div>
        </div>
      )}

      {/* Help Text */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-gray-900 mb-2">How it works</h4>
        <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
          <li>Subscribe to a <strong>district</strong> for district-wide events (holidays, PD days)</li>
          <li>Subscribe to individual <strong>schools</strong> for school-specific events</li>
          <li>Events automatically appear in your family calendar</li>
          <li><strong>Custom schools</strong> let you track schools not yet in our system</li>
          <li><strong>Request a school</strong> and we'll add calendar integration for it</li>
        </ul>
      </div>
    </div>
  )
}
